// The production plan as the picker sees it (E32-S9-T3; ADR-032): the answer shapes, and the budget
// arithmetic that decides, before anything is sent, whether a selection may grow. The server decides
// again on save (validatePlan); this is so a refusal can be shown at the click, with its reason.
import {
  ProductionPlan,
  ProductionPlanSummary,
  segmentKey,
  TitleTier,
  type ProductionPlanSegment,
} from "@waypoint/contracts";
import { z } from "zod";

export const PLAN_PATH = "/v1/production-plan";

/** One segment of the catalogue: the reader's shape (apps/api catalogue.ts), as the route sends it. */
export const CatalogueSegment = z.strictObject({
  packId: z.string(),
  titleId: z.string(),
  title: z.string(),
  tier: TitleTier,
  plannedRuntimeSeconds: z.number().int().optional(),
  runtimeSeconds: z.number().int().optional(),
  reference: z.boolean(),
});

/** PUT's answer. `plan` is null only on a GET before anything is saved. */
export const PlanWritten = z.strictObject({
  plan: ProductionPlan.nullable(),
  summary: ProductionPlanSummary,
});
export type PlanWritten = z.infer<typeof PlanWritten>;

/** GET's answer: the plan, its summary and every segment of every imported pack. */
export const PlanRead = PlanWritten.extend({ catalogue: z.array(CatalogueSegment) });
export type PlanRead = z.infer<typeof PlanRead>;

export type TierName = z.infer<typeof TitleTier>;
export const TIERS: readonly TierName[] = TitleTier.options;

export type RuntimeKind = "measured" | "estimated";

/** A catalogue segment with the seconds it counts today. */
export interface Entry {
  readonly key: string;
  readonly packId: string;
  readonly titleId: string;
  readonly title: string;
  readonly tier: TierName;
  /** The measured runtime once there is one, else the planned one. */
  readonly seconds: number;
  readonly planned: number;
  readonly kind: RuntimeKind;
  readonly reference: boolean;
}

export function entriesOf(catalogue: PlanRead["catalogue"]): Entry[] {
  return catalogue.map((c) => {
    const planned = c.plannedRuntimeSeconds ?? 0;
    return {
      key: segmentKey(c),
      packId: c.packId,
      titleId: c.titleId,
      title: c.title,
      tier: c.tier,
      seconds: c.runtimeSeconds ?? planned,
      planned,
      kind: c.runtimeSeconds === undefined ? "estimated" : "measured",
      reference: c.reference,
    };
  });
}

/** m:ss, the way the mockup writes a runtime. */
export function mmss(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  return `${String(Math.floor(s / 60))}:${String(s % 60).padStart(2, "0")}`;
}

/** The picker's arithmetic for one screen: the catalogue, the budget, and the stored plan's seconds. */
export interface Ledger {
  readonly byKey: ReadonlyMap<string, Entry>;
  readonly budget: number;
  /** What the stored plan counts now (the server's summary): above the budget once estimates become measured. */
  readonly storedUsed: number;
}

export function usedOf(ledger: Ledger, keys: ReadonlySet<string>): number {
  let used = 0;
  for (const key of keys) used += ledger.byKey.get(key)?.seconds ?? 0;
  return used;
}

/**
 * The most a selection may use. Normally the budget; while the stored plan is itself over it, what it
 * already uses, so the owner can remove segments or swap one for a shorter one (ADR-032 decision 2).
 */
export function ceilingOf(ledger: Ledger): number {
  return Math.max(ledger.budget, ledger.storedUsed);
}

export function fits(ledger: Ledger, selected: ReadonlySet<string>, entry: Entry): boolean {
  return usedOf(ledger, selected) + entry.seconds <= ceilingOf(ledger);
}

export type Refusal =
  | { readonly kind: "room"; readonly entry: Entry; readonly left: number; readonly total: number }
  | { readonly kind: "over"; readonly entry: Entry; readonly over: number; readonly total: number };

/** Why adding `entry` is refused, in figures; the page words it. */
export function refusalFor(ledger: Ledger, selected: ReadonlySet<string>, entry: Entry): Refusal {
  const used = usedOf(ledger, selected);
  const total = used + entry.seconds;
  if (ledger.storedUsed > ledger.budget)
    return { kind: "over", entry, over: ledger.storedUsed - ledger.budget, total };
  return { kind: "room", entry, left: Math.max(0, ledger.budget - used), total };
}

export function keysOf(segments: readonly ProductionPlanSegment[]): Set<string> {
  return new Set(segments.map(segmentKey));
}

export function sameSet(a: ReadonlySet<string>, b: ReadonlySet<string>): boolean {
  return a.size === b.size && [...a].every((k) => b.has(k));
}

export function segmentOf(entry: Pick<Entry, "packId" | "titleId">): ProductionPlanSegment {
  return { packId: entry.packId, titleId: entry.titleId };
}

const WHEN = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  timeZone: "UTC",
});

/** "28 Oct 2026, 11:30 UTC": the time a plan was picked, the same for everyone who reads it. */
export function whenOf(iso: string): string {
  return `${WHEN.format(new Date(iso))} UTC`;
}
